import { useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { PlaceTypeahead } from "../components/PlaceTypeahead";
import { apiFetch } from "../lib/api";
import { personLabel, suggestTreeMatches } from "../lib/treeMatch";
import { useMe } from "../hooks/useMe";
import { useHeaderSearch } from "../lib/headerSearch";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can, ROLES } from "@family/shared";
import type {
  DeniedUsersResponse,
  FamilyMembersResponse,
  PendingUser,
  PendingUsersResponse,
  PersonsListResponse,
  PlaceSuggestion,
  RelationshipsListResponse,
  Role,
} from "@family/shared";


export function MembersPage() {
  const meQuery = useMe();

  const primary = resolveMembership(meQuery.data);
  const membersQuery = useQuery<FamilyMembersResponse>({
    queryKey: ["members", primary?.familySlug],
    queryFn: () =>
      apiFetch<FamilyMembersResponse>(
        `/families/${primary!.familySlug}/members`,
      ),
    enabled: Boolean(primary) && can(primary!.role, "members:view"),
  });

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (!primary || !can(primary.role, "members:view")) {
    return <Navigate to="/" replace />;
  }

  // Moderators can view members and approve pending users (capped to
  // contributor/viewer server-side); everything else stays admin-only.
  const canManage = can(primary.role, "members:manage");
  const canLinkTree = can(primary.role, "members:link-tree");

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <h1>Members — {primary.familyName}</h1>

        <PendingUsersSection
          familySlug={primary.familySlug}
          canManage={canManage}
          canLinkTree={canLinkTree}
        />

        {membersQuery.isLoading && <p className="text-muted">Loading…</p>}
        {membersQuery.error && (
          <p className="text-error">
            Error: {(membersQuery.error as Error).message}
          </p>
        )}
        {membersQuery.data && (
          <MembersList
            members={membersQuery.data.members}
            familySlug={primary.familySlug}
            currentUserId={meQuery.data?.user.id ?? ""}
            canManage={canManage}
            canLinkTree={canLinkTree}
          />
        )}

        {canManage && <DeniedUsersSection familySlug={primary.familySlug} />}
      </main>
    </>
  );
}

/**
 * Denied sign-ups, collapsed at the bottom of the page. Deny used to be a
 * one-way door: the person kept seeing "you're in the queue" while being
 * silently blocked, with no way for an admin to change their mind.
 */
function DeniedUsersSection({ familySlug }: { familySlug: string }) {
  const queryClient = useQueryClient();
  const deniedQuery = useQuery<DeniedUsersResponse>({
    queryKey: ["denied-users", familySlug],
    queryFn: () =>
      apiFetch<DeniedUsersResponse>(`/families/${familySlug}/members/denied`),
  });
  const restore = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${familySlug}/members/deny/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["denied-users", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["pending-users", familySlug] });
    },
  });

  const users = deniedQuery.data?.users ?? [];
  if (users.length === 0) return null;

  return (
    <details style={{ marginTop: "var(--s-4)", minWidth: 0 }}>
      <summary style={{ cursor: "pointer", fontWeight: 600 }}>
        Denied sign-ups{" "}
        <span className="badge" style={{ marginLeft: 8 }}>{users.length}</span>
      </summary>
      <div className="stack" style={{ marginTop: "var(--s-2)" }}>
      <p className="text-muted" style={{ fontSize: "0.9rem", margin: 0 }}>
        These people signed in but were denied. They still see "you're in the
        queue" and can't re-apply themselves. Restore puts them back under
        Pending, where they can be approved or denied again.
      </p>
      <ul className="list-unstyled stack" style={{ gap: "var(--s-2)" }}>
        {users.map((u) => (
          <li key={u.id} className="card row-between" style={{ padding: "10px 16px" }}>
            <div>
              <strong>{u.displayName ?? u.email ?? u.phone ?? u.id.slice(0, 8)}</strong>
              <div className="text-subtle" style={{ fontSize: "0.85rem" }}>
                {u.email ?? u.phone ?? "—"} · denied{" "}
                {new Date(u.deniedAt).toLocaleDateString()}
                {u.deniedByName ? ` by ${u.deniedByName}` : ""}
              </div>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => restore.mutate(u.id)}
              disabled={restore.isPending}
              style={{ padding: "6px 14px", fontSize: "0.85rem", minHeight: 34 }}
            >
              Restore to pending
            </button>
          </li>
        ))}
      </ul>
      {restore.error && <p className="text-error">{restore.error.message}</p>}
      </div>
    </details>
  );
}

function MembersList({
  members,
  familySlug,
  currentUserId,
  canManage,
  canLinkTree,
}: {
  members: FamilyMembersResponse["members"];
  familySlug: string;
  currentUserId: string;
  canManage: boolean;
  canLinkTree: boolean;
}) {
  const queryClient = useQueryClient();

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${familySlug}/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members", familySlug] });
    },
  });

  // Filter by anything an admin might remember: name, contact, place,
  // role, or the tree person they're linked to. Every word must match.
  const [filter, setFilter] = useState("");
  const filterRef = useRef<HTMLInputElement>(null);
  useHeaderSearch("members", () => {
    filterRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    filterRef.current?.focus();
  });
  const shown = useMemo(() => {
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return members;
    return members.filter((m) => {
      const hay = [
        m.firstName,
        m.lastName,
        m.displayName,
        m.email,
        m.phone,
        m.city,
        m.country,
        m.placeDisplayName,
        m.role,
        m.linkedPersonName,
      ]
        .filter((v): v is string => Boolean(v))
        .join(" ")
        .toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [members, filter]);

  return (
    <div className="stack">
      <div className="row-between" style={{ flexWrap: "wrap", gap: "var(--s-2)" }}>
        <h2 style={{ margin: 0 }}>
          Members{" "}
          <span className="text-muted" style={{ fontWeight: 400, fontSize: "0.9rem" }}>
            {filter.trim() ? `${shown.length} of ${members.length}` : members.length}
          </span>
        </h2>
        <input
          ref={filterRef}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setFilter("");
          }}
          placeholder="Find a member…"
          aria-label="Find a member"
          className="member-filter"
        />
      </div>
      {filter.trim() && shown.length === 0 && (
        <p className="text-muted">No members match “{filter.trim()}”.</p>
      )}
    <ul className="list-unstyled stack">
      {shown.map((m) => (
        <MemberCard
          key={m.userId}
          member={m}
          familySlug={familySlug}
          canManage={canManage}
          canLinkTree={canLinkTree}
          isCurrentUser={m.userId === currentUserId}
          onRemove={() => {
            const name = m.displayName ?? m.email ?? "this member";
            if (
              window.confirm(
                `Remove ${name} from this family? Their account and any other family memberships are kept — they'll reappear under Pending, where you can Deny them.`,
              )
            ) {
              removeMember.mutate(m.userId);
            }
          }}
          isRemoving={removeMember.isPending}
        />
      ))}
      {removeMember.error && (
        <p className="text-error" style={{ marginTop: 8 }}>
          {removeMember.error.message}
        </p>
      )}
    </ul>
    </div>
  );
}

function MemberCard({
  member: m,
  familySlug,
  canManage,
  canLinkTree,
  isCurrentUser,
  onRemove,
  isRemoving,
}: {
  member: FamilyMembersResponse["members"][number];
  familySlug: string;
  canManage: boolean;
  canLinkTree: boolean;
  isCurrentUser: boolean;
  onRemove: () => void;
  isRemoving: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  // Pre-fill names from firstName/lastName if set, else best-effort split
  // of displayName (mirrors what ProfilePage does on first onboarding so
  // an admin opening "Edit profile" sees the same starting point the
  // user themselves would see).
  const splitDisplay = (m.displayName ?? "").trim().split(/\s+/);
  const initialFirst = m.firstName ?? splitDisplay[0] ?? "";
  const initialLast =
    m.lastName ??
    (splitDisplay.length > 1 ? splitDisplay.slice(1).join(" ") : "");

  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [bio, setBio] = useState(m.bio ?? "");
  const [placeText, setPlaceText] = useState(
    m.placeDisplayName ??
      [m.city, m.country].filter((s): s is string => Boolean(s)).join(", "),
  );
  const [pickedPlace, setPickedPlace] = useState<PlaceSuggestion | null>(null);
  const [placeWasPicked, setPlaceWasPicked] = useState(
    m.placeLat != null && m.placeLng != null,
  );
  const [role, setRole] = useState<Role>(m.role);

  const patchMember = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch(`/families/${familySlug}/members/${m.userId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["members", familySlug],
      });
      setEditing(false);
    },
  });

  function saveRole(next: Role) {
    if (next === m.role) return;
    setRole(next);
    patchMember.mutate({ role: next });
  }

  function saveProfile() {
    let placeFields: Record<string, unknown>;
    if (pickedPlace) {
      placeFields = {
        city: pickedPlace.city ?? null,
        country: pickedPlace.country ?? null,
        placeDisplayName: pickedPlace.displayName,
        placeLat: pickedPlace.lat,
        placeLng: pickedPlace.lng,
      };
    } else if (placeText.trim()) {
      const parts = placeText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      placeFields = {
        city: parts[0] || null,
        country: parts.length > 1 ? parts[parts.length - 1]! : null,
        placeDisplayName: null,
        placeLat: null,
        placeLng: null,
      };
    } else {
      placeFields = {
        city: null,
        country: null,
        placeDisplayName: null,
        placeLat: null,
        placeLng: null,
      };
    }

    patchMember.mutate({
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      bio: bio.trim() || null,
      ...placeFields,
    });
  }

  const labelName =
    [m.firstName, m.lastName].filter((s): s is string => Boolean(s)).join(" ") ||
    m.displayName ||
    m.email ||
    m.phone ||
    m.userId;

  const locationDisplay =
    m.placeDisplayName ||
    [m.city, m.country].filter(Boolean).join(", ") ||
    "—";

  return (
    <li className="card stack" style={{ gap: "var(--s-2)" }}>
      <div
        className="row-between"
        style={{ flexWrap: "wrap", gap: "var(--s-3)" }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong>{labelName}</strong>
          <div
            className="text-subtle"
            style={{
              fontSize: "0.85rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {m.email ?? m.phone ?? "—"}
          </div>
          <div
            className="text-subtle"
            style={{ fontSize: "0.8rem", marginTop: 2 }}
          >
            📍 {locationDisplay}
          </div>
          {m.addedByName && (
            <div
              className="text-subtle"
              style={{ fontSize: "0.75rem", marginTop: 2 }}
            >
              ✓ Approved by {m.addedByName}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "var(--s-2)",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {!canManage || (isCurrentUser && m.role === "admin") ? (
            <span className="badge badge--admin">{m.role}</span>
          ) : (
            <select
              value={role}
              onChange={(e) => saveRole(e.target.value as Role)}
              disabled={patchMember.isPending}
              style={{
                padding: "6px 10px",
                fontSize: "0.85rem",
                border: "1px solid var(--c-border-strong)",
                borderRadius: 6,
                background: "var(--c-bg)",
                color: "var(--c-text)",
              }}
              aria-label={`Role for ${labelName}`}
            >
              {ROLES.filter((r) => r !== "site_admin").map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}

          {canManage && !editing && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEditing(true)}
              style={{
                padding: "4px 10px",
                fontSize: "0.8rem",
                minHeight: 28,
              }}
            >
              Edit profile
            </button>
          )}

          {canManage && !isCurrentUser && m.role !== "admin" && (
            <button
              type="button"
              className="btn-tertiary"
              onClick={onRemove}
              disabled={isRemoving}
              style={{
                padding: "4px 8px",
                fontSize: "0.8rem",
                minHeight: 28,
                color: "var(--c-danger)",
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <MemberTreeLink member={m} familySlug={familySlug} canLinkTree={canLinkTree} />

      {editing && (
        <div
          className="stack"
          style={{
            gap: "var(--s-2)",
            paddingTop: "var(--s-2)",
            borderTop: "1px solid var(--c-border)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "var(--s-2)",
            }}
          >
            <div>
              <label style={{ fontSize: "0.8rem" }}>First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={120}
                style={{ minHeight: 36, fontSize: "0.9rem" }}
              />
            </div>
            <div>
              <label style={{ fontSize: "0.8rem" }}>Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={120}
                style={{ minHeight: 36, fontSize: "0.9rem" }}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: "0.8rem" }}>Bio</label>
            <textarea
              rows={3}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={2000}
              style={{ fontSize: "0.9rem" }}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.8rem" }}>Where they live</label>
            <PlaceTypeahead
              value={placeText}
              onChange={(text) => {
                setPlaceText(text);
                if (pickedPlace) setPickedPlace(null);
              }}
              onPick={(place) => {
                setPickedPlace(place);
                setPlaceWasPicked(place !== null);
              }}
              hasPickedPlace={Boolean(pickedPlace) || placeWasPicked}
              placeholder="Start typing a city — pick from suggestions"
            />
          </div>
          <div style={{ display: "flex", gap: "var(--s-2)" }}>
            <button
              type="button"
              onClick={saveProfile}
              disabled={patchMember.isPending}
              style={{
                padding: "6px 14px",
                fontSize: "0.85rem",
                minHeight: 32,
              }}
            >
              {patchMember.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setFirstName(initialFirst);
                setLastName(initialLast);
                setBio(m.bio ?? "");
                setPlaceText(
                  m.placeDisplayName ??
                    [m.city, m.country]
                      .filter((s): s is string => Boolean(s))
                      .join(", "),
                );
                setPickedPlace(null);
                setPlaceWasPicked(m.placeLat != null && m.placeLng != null);
                setEditing(false);
              }}
              disabled={patchMember.isPending}
              style={{
                padding: "6px 14px",
                fontSize: "0.85rem",
                minHeight: 32,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {patchMember.error && (
        <p className="text-error" style={{ fontSize: "0.85rem" }}>
          {patchMember.error.message}
        </p>
      )}
    </li>
  );
}

// Shows which tree person a member is linked to, and lets the admin re-link
// it — the fix for "I claimed the wrong person and can't find my real entry".
// Search typeahead mirrors TreeSelfLink; the API call targets the member
// instead of the caller, and a 409 (person held by another member) asks
// before retrying with override.
function MemberTreeLink({
  member: m,
  familySlug,
  canLinkTree,
}: {
  member: FamilyMembersResponse["members"][number];
  familySlug: string;
  canLinkTree: boolean;
}) {
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");

  const results = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug, search],
    queryFn: () =>
      apiFetch<PersonsListResponse>(
        `/families/${familySlug}/persons?q=${encodeURIComponent(search)}`,
      ),
    enabled: picking && search.trim().length >= 2,
  });

  const link = useMutation<
    { linkedPersonId: string | null },
    Error,
    { personId: string | null; override?: boolean }
  >({
    mutationFn: (body) =>
      apiFetch(`/families/${familySlug}/members/${m.userId}/person`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members", familySlug] });
      setPicking(false);
      setSearch("");
    },
  });

  async function linkTo(personId: string | null) {
    try {
      await link.mutateAsync({ personId });
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("API 409")) {
        if (
          window.confirm(
            "That person is already linked to another member. Link anyway? The other member will lose their tree link.",
          )
        ) {
          await link.mutateAsync({ personId, override: true }).catch(() => {});
        } else {
          link.reset();
        }
      }
    }
  }

  const candidates = (results.data?.persons ?? []).slice(0, 6);

  return (
    <div className="stack" style={{ gap: "var(--s-1)" }}>
      <div
        style={{
          display: "flex",
          gap: "var(--s-2)",
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: "0.85rem",
        }}
      >
        <span>
          🌳{" "}
          {m.linkedPersonName ? (
            <>
              In tree as <strong>{m.linkedPersonName}</strong>
            </>
          ) : (
            <span className="text-subtle">Not linked to the tree</span>
          )}
        </span>
        {canLinkTree && !picking && (
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => setPicking(true)}
            style={{ padding: "2px 8px", fontSize: "0.8rem", minHeight: 24 }}
          >
            {m.linkedPersonId ? "Change" : "Link to tree…"}
          </button>
        )}
        {canLinkTree && !picking && m.linkedPersonId && (
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => void linkTo(null)}
            disabled={link.isPending}
            style={{
              padding: "2px 8px",
              fontSize: "0.8rem",
              minHeight: 24,
              color: "var(--c-danger)",
            }}
          >
            Unlink
          </button>
        )}
      </div>

      {picking && (
        <div className="stack" style={{ gap: 4, maxWidth: 420 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a name in the tree…"
            aria-label={`Search the tree for a person to link to ${
              m.displayName ?? m.email ?? "this member"
            }`}
            style={{ minHeight: 34, fontSize: "0.9rem" }}
          />
          {search.trim().length >= 2 &&
            candidates.length === 0 &&
            !results.isLoading && (
              <p className="text-muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                No match — add them to the tree first.
              </p>
            )}
          {candidates.map((p) => (
            <button
              key={p.id}
              type="button"
              className="btn-secondary"
              onClick={() => void linkTo(p.id)}
              disabled={link.isPending}
              style={{
                justifyContent: "flex-start",
                textAlign: "left",
                padding: "4px 10px",
                fontSize: "0.85rem",
                minHeight: 30,
              }}
            >
              {[p.givenName, p.familyName].filter(Boolean).join(" ") ||
                "Unnamed"}
              {p.birthDate ? ` (${p.birthDate.slice(0, 4)})` : ""}
            </button>
          ))}
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => {
              setPicking(false);
              setSearch("");
              link.reset();
            }}
            style={{
              alignSelf: "flex-start",
              padding: "2px 8px",
              fontSize: "0.8rem",
              minHeight: 24,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {link.error && (
        <p className="text-error" style={{ margin: 0, fontSize: "0.85rem" }}>
          {link.error.message}
        </p>
      )}
    </div>
  );
}

function PendingUsersSection({
  familySlug,
  canManage,
  canLinkTree,
}: {
  familySlug: string;
  canManage: boolean;
  canLinkTree: boolean;
}) {
  const queryClient = useQueryClient();

  const pendingQuery = useQuery<PendingUsersResponse>({
    queryKey: ["pending-users", familySlug],
    queryFn: () => apiFetch<PendingUsersResponse>(`/families/${familySlug}/members/pending`),
  });

  // Approve, then (when a tree person was picked) link in the same gesture.
  // The link is best-effort: a decline on the 409 confirm still leaves the
  // member approved, just unlinked — same end state as the old two-step flow.
  const addMember = useMutation({
    mutationFn: async (params: {
      userId: string;
      role: Role;
      personId: string | null;
    }) => {
      await apiFetch(`/families/${familySlug}/members`, {
        method: "POST",
        body: JSON.stringify({ userId: params.userId, role: params.role }),
      });
      if (!params.personId) return;
      const linkUrl = `/families/${familySlug}/members/${params.userId}/person`;
      try {
        await apiFetch(linkUrl, {
          method: "PUT",
          body: JSON.stringify({ personId: params.personId }),
        });
      } catch (err) {
        if (err instanceof Error && err.message.startsWith("API 409")) {
          if (
            window.confirm(
              "That person is already linked to another member. Link anyway? The other member will lose their tree link.",
            )
          ) {
            await apiFetch(linkUrl, {
              method: "PUT",
              body: JSON.stringify({ personId: params.personId, override: true }),
            });
          }
          return;
        }
        throw new Error(
          `Member was approved, but the tree link failed — link them from their member card below. (${
            err instanceof Error ? err.message : String(err)
          })`,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["pending-users", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["members", familySlug] });
    },
  });

  const denyUser = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${familySlug}/members/deny`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pending-users", familySlug] });
    },
  });

  const users = pendingQuery.data?.users ?? [];

  // Whole tree, once, for client-side matching of each sign-up's answers
  // (own name, parents' names, branch). Same cache keys as the tree page.
  const treeEnabled = canLinkTree && users.length > 0;
  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug],
    queryFn: () => apiFetch<PersonsListResponse>(`/families/${familySlug}/persons`),
    enabled: treeEnabled,
  });
  const relsQuery = useQuery<RelationshipsListResponse>({
    queryKey: ["relationships", familySlug],
    queryFn: () =>
      apiFetch<RelationshipsListResponse>(`/families/${familySlug}/relationships`),
    enabled: treeEnabled,
  });

  if (pendingQuery.isLoading) return null;
  if (users.length === 0) return null;

  return (
    <section className="stack pending-section">
      <h2 style={{ margin: 0 }}>
        ⏳ Pending approval{" "}
        <span className="badge badge--pending" style={{ marginLeft: 8 }}>
          {users.length}
        </span>
      </h2>
      <p className="text-muted" style={{ fontSize: "0.9rem" }}>
        These users have signed in but aren't members of this family yet. Add them with a role.
      </p>
      <ul className="list-unstyled">
        {users.map((u) => (
          <PendingUserCard
            key={u.id}
            user={u}
            familySlug={familySlug}
            canManage={canManage}
            canLinkTree={canLinkTree}
            persons={personsQuery.data?.persons ?? []}
            relationships={relsQuery.data?.relationships ?? []}
            onAdd={(role, personId) =>
              addMember.mutate({ userId: u.id, role, personId })
            }
            onDeny={() => denyUser.mutate(u.id)}
            isPending={addMember.isPending || denyUser.isPending}
          />
        ))}
      </ul>
      {(addMember.error || denyUser.error) && (
        <p className="text-error">{(addMember.error ?? denyUser.error)?.message}</p>
      )}
    </section>
  );
}

function PendingUserCard({
  user,
  familySlug,
  canManage,
  canLinkTree,
  persons,
  relationships,
  onAdd,
  onDeny,
  isPending,
}: {
  user: PendingUser;
  familySlug: string;
  canManage: boolean;
  canLinkTree: boolean;
  persons: PersonsListResponse["persons"];
  relationships: RelationshipsListResponse["relationships"];
  onAdd: (role: Role, personId: string | null) => void;
  onDeny: () => void;
  isPending: boolean;
}) {
  const [role, setRole] = useState<Role>("contributor");
  const [search, setSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState<
    PersonsListResponse["persons"][number] | null
  >(null);

  // Pre-searched with the sign-up name so likely tree matches show as
  // one-click chips — seeing "in the tree as X" is itself a vetting signal
  // before approving. Same cache key as MemberTreeLink's typeahead.
  const results = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug, search],
    queryFn: () =>
      apiFetch<PersonsListResponse>(
        `/families/${familySlug}/persons?q=${encodeURIComponent(search)}`,
      ),
    enabled: canLinkTree && search.trim().length >= 2,
  });

  const candidates = (results.data?.persons ?? []).slice(0, 4);
  const chips =
    selectedPerson && !candidates.some((p) => p.id === selectedPerson.id)
      ? [selectedPerson, ...candidates]
      : candidates;

  // What the applicant told us, matched against the tree with a reason per
  // hit ("child of Yonas Kidane"). Manual search below stays for the rest.
  const suggestions = useMemo(
    () =>
      canLinkTree && persons.length > 0
        ? suggestTreeMatches(user, persons, relationships)
        : [],
    [canLinkTree, user, persons, relationships],
  );
  const branchLabel =
    user.branchStatus === "descendant"
      ? (user.branchName ?? "—")
      : user.branchStatus === "married_in"
        ? "Married into the family"
        : user.branchStatus === "not_sure"
          ? "Not sure"
          : null;
  const hasHints = Boolean(
    branchLabel || user.motherName || user.fatherName || user.knownRelative,
  );
  // A half-finished sign-up: signed in, never saved the onboarding form.
  // There is no name and no tree hints because the questions were never
  // answered — say so rather than showing an anonymous, empty card.
  // `=== false` on purpose: if a web deploy lands ahead of the API the field
  // is simply absent, and every card would otherwise claim to be unfinished.
  const unfinished = user.onboarded === false;

  // Moderators may only grant contributor/viewer (enforced server-side too).
  const grantableRoles = canManage
    ? ROLES.filter((r) => r !== "site_admin")
    : (["contributor", "viewer"] as const);

  return (
    <li className="card stack" style={{ gap: "var(--s-2)" }}>
      <div className="row-between">
        <div>
          <strong>{user.displayName ?? user.email ?? user.phone ?? user.id.slice(0, 8)}</strong>
          <div className="text-subtle" style={{ fontSize: "0.85rem" }}>
            {user.email ?? user.phone ?? "—"}
          </div>
          {unfinished && (
            <div
              className="text-subtle"
              style={{ fontSize: "0.8rem", marginTop: 2 }}
            >
              ⏳ Hasn't finished signing up — signed in{" "}
              {new Date(user.createdAt).toLocaleDateString()}, never filled in
              the name or tree questions.
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "var(--s-2)", alignItems: "center" }}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={isPending}
            style={{
              padding: "6px 10px", fontSize: "0.85rem",
              border: "1px solid var(--c-border-strong)", borderRadius: 6,
              background: "var(--c-bg)", color: "var(--c-text)",
            }}
          >
            {grantableRoles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onAdd(role, selectedPerson?.id ?? null)}
            disabled={isPending}
            style={{ padding: "6px 14px", fontSize: "0.85rem", minHeight: 34, borderRadius: 6 }}
          >
            {isPending ? "…" : selectedPerson ? "Add & link" : "Add"}
          </button>
          {canManage && (
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => {
              if (window.confirm(`Deny ${user.displayName ?? user.email ?? "this user"}? They won't appear in pending again.`)) {
                onDeny();
              }
            }}
            disabled={isPending}
            style={{ padding: "6px 10px", fontSize: "0.85rem", minHeight: 34, color: "var(--c-danger)" }}
          >
            Deny
          </button>
          )}
        </div>
      </div>

      {hasHints && (
        <dl className="signup-facts">
          <dt>Branch</dt>
          <dd className={branchLabel ? "" : "text-subtle"}>{branchLabel ?? "—"}</dd>
          <dt>Mother</dt>
          <dd className={user.motherName ? "" : "text-subtle"}>{user.motherName ?? "—"}</dd>
          <dt>Father</dt>
          <dd className={user.fatherName ? "" : "text-subtle"}>{user.fatherName ?? "—"}</dd>
          <dt>Knows</dt>
          <dd className={user.knownRelative ? "" : "text-subtle"}>{user.knownRelative ?? "—"}</dd>
        </dl>
      )}

      {canLinkTree && suggestions.length > 0 && (
        <div className="stack" style={{ gap: "var(--s-1)", fontSize: "0.85rem" }}>
          <span>
            🌳 Suggested from the tree
            {user.branchStatus === "descendant" && user.branchName && (
              <span className="text-subtle"> · {user.branchName} branch first</span>
            )}
            :
          </span>
          <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
            {suggestions.map((sug) => {
              const p = sug.person;
              const isSelected = selectedPerson?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelectedPerson(isSelected ? null : p)}
                  disabled={isPending}
                  className="tree-chip"
                  style={{
                    background: isSelected ? "var(--c-accent)" : "transparent",
                    color: isSelected ? "var(--c-accent-contrast)" : "var(--c-text)",
                    border: isSelected
                      ? "1px solid var(--c-accent)"
                      : "1px solid var(--c-border-strong)",
                  }}
                >
                  {personLabel(p)}
                  <small style={{ opacity: 0.75, marginLeft: 6, fontWeight: 400 }}>
                    {sug.reason}
                  </small>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {canLinkTree && (
        <div
          className="stack"
          style={{ gap: "var(--s-1)", fontSize: "0.85rem" }}
        >
          <div
            style={{
              display: "flex",
              gap: "var(--s-2)",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span>🌳 {suggestions.length > 0 ? "Or search the tree:" : "Link to tree (optional):"}</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a name in the tree…"
              aria-label={`Search the tree for a person to link to ${
                user.displayName ?? user.email ?? "this user"
              }`}
              disabled={isPending}
              style={{
                minHeight: 32,
                fontSize: "0.85rem",
                padding: "4px 10px",
                width: "auto",
                flex: "0 1 240px",
              }}
            />
          </div>
          {chips.length > 0 && (
            <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
              {chips.map((p) => {
                const isSelected = selectedPerson?.id === p.id;
                const label =
                  ([p.givenName, p.familyName].filter(Boolean).join(" ") ||
                    "Unnamed") + (p.birthDate ? ` (${p.birthDate.slice(0, 4)})` : "");
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelectedPerson(isSelected ? null : p)}
                    disabled={isPending}
                    style={{
                      padding: "3px 12px",
                      fontSize: "0.8rem",
                      minHeight: 28,
                      borderRadius: "var(--r-pill)",
                      background: isSelected ? "var(--c-accent)" : "transparent",
                      color: isSelected ? "var(--c-accent-contrast)" : "var(--c-text)",
                      border: isSelected
                        ? "1px solid var(--c-accent)"
                        : "1px solid var(--c-border-strong)",
                      fontWeight: 500,
                    }}
                  >
                    {isSelected ? "✓ " : ""}
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {search.trim().length >= 2 &&
            chips.length === 0 &&
            !results.isLoading && (
              <p className="text-subtle" style={{ margin: 0, fontSize: "0.8rem" }}>
                No tree match — you can approve now and link them later.
              </p>
            )}
        </div>
      )}
    </li>
  );
}
